import 'hammerjs';
import {scaleQuantize} from "d3-scale";

interface ITouchResizeScope extends ng.IScope {
    close: () => void;
}

/**
 * A directive to enhance the mobile pane
 *
 * The pane can be closed, partially open to a fraction of the screen, or opened to the whole screen.
 * The pane will snap between these snaps if the pane title is dragged and released with some velocity.
 */
export class TouchResize implements ng.IDirective {
    // restrict: 'A';
    scope: {
        close: '&closeAction';
    };
    link: ($scope: ITouchResizeScope, elem: ng.IAugmentedJQuery,
           attrs: ng.IAttributes) => void = null;

    constructor($timeout: ng.ITimeoutService) {

        this.link = (scope: ITouchResizeScope, elem: ng.IAugmentedJQuery,
                     attrs: ng.IAttributes) => {
            let elementToBeResized = elem[0];
            let touchResizeHandles = elementToBeResized.querySelectorAll('[touch-resize-handle]');

            const transitionClass = 'pane-transition-height';

            // the element the user will touch and drag maybe a child element of the element being resized
            const handle = touchResizeHandles.length ? (<HTMLElement> touchResizeHandles.item(0)) : elem[0];
            const hammer = new Hammer(handle, {});

            let panning = false;
            handle.addEventListener('click', () => {
                if (!panning) { // don't fire if the pan gesture is just finishing
                    
                    elementToBeResized.classList.remove(transitionClass);
                    elementToBeResized.style.height = '';
                }
            });

            hammer.get('pan').set({
                direction: Hammer.DIRECTION_VERTICAL
            });

            let heightAtStartOfPan = 0;

            let windowHeight = 0;



            // The pane has three sticky notches,
            // closed (height=0), open midway (out midPoint)
            // and fully open to the height of the window
            const midPoint = 0.4;

            // Maps a point on the y axis to the next notch above that point
            const snapUp = scaleQuantize()
                .range([midPoint, 1]);

            // Maps to the next notch below a point on the y axis
            const snapDown = scaleQuantize()
                .range([0, midPoint]);

            hammer.on('panstart', (event) => {
                windowHeight = window.innerHeight;
                panning = true;
                // Our scales have a domain in window space
                snapUp.domain([0, windowHeight]);
                snapDown.domain([0, windowHeight]);

                heightAtStartOfPan = elementToBeResized.clientHeight;
                elementToBeResized.classList.remove(transitionClass);
            });

            hammer.on('pan', (event) => {
                let newHeight = Math.min(heightAtStartOfPan - event.deltaY, windowHeight);
                elementToBeResized.style.height = `${newHeight}px`;
            });

            const THRESHOLD_VELOCITY = 1;
            const MIN_HEIGHT = 50;

            hammer.on('panend', (event) => {
                let newHeight : number = undefined;
                let eh = elementToBeResized.offsetHeight;

                if (event.velocityY < -THRESHOLD_VELOCITY) {
                    // snap the pane upwards to the next notch
                    newHeight = snapUp(eh);
                } else if (event.velocityY > THRESHOLD_VELOCITY) {
                    // snap down to the next notch
                    newHeight = snapDown(eh);
                } else if (eh < MIN_HEIGHT) {
                    // almost closed, so the pane should be properly closed
                    newHeight = 0;
                }

                if (newHeight !== undefined) {
                    let heightStr = newHeight == midPoint ? '' : `${newHeight * windowHeight}px`;
                    elementToBeResized.classList.add(transitionClass);
                    elementToBeResized.style.height = heightStr;
                    if (newHeight == 0 && attrs['closeAction']) {
                        $timeout(() => {
                            scope.$eval(attrs['closeAction']);
                            elementToBeResized.style.height = '';
                            elementToBeResized.classList.remove(transitionClass);
                        }, 500);
                    }
                }

                //  trying to prevent the click handler firing. Gross, but works?
                requestAnimationFrame(() => panning = false);
            });
        }
    }


    static factory() : ng.IDirectiveFactory {
        const directive = ($timeout: ng.ITimeoutService) => new TouchResize($timeout);
        directive.$inject = ['$timeout'];
        return directive;
    }
}